import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  CloudDownload as IngestIcon,
  CheckCircle as ConnectedIcon,
  Error as DisconnectedIcon,
  HelpOutline as UnknownIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import AgentConfigurationDialog from './AgentConfigurationDialog';
import { usePermission } from '../../hooks/usePermission';
import { formatRelativeTime, formatDateTime } from '../../utils/dateUtils';
import {
  getVendorLabel,
  getVendorIcon,
  CAPABILITY_LABELS,
  computeSuccessRate,
  capabilitiesForType,
  describeApiError,
} from '../Integrations/agentMeta';

/**
 * Agent list with test / ingest / edit / toggle / delete actions.
 *
 * Standalone (Settings page) it loads its own agents. The Integrations page
 * passes `agents`, `types` and `onReload` so the list is shared across tabs.
 */
const AgentSettings = ({ agents: externalAgents, types: externalTypes, onReload, loading: externalLoading, hideHeader = false }) => {
  const { canManageAgents } = usePermission();
  const controlled = Array.isArray(externalAgents);

  const [internalAgents, setInternalAgents] = useState([]);
  const [internalTypes, setInternalTypes] = useState([]);
  const [internalLoading, setInternalLoading] = useState(!controlled);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [forceDeleteInfo, setForceDeleteInfo] = useState(null);
  const [busy, setBusy] = useState({});
  const [ingestResult, setIngestResult] = useState(null);

  const agents = controlled ? externalAgents : internalAgents;
  const types = externalTypes || internalTypes;
  const loading = controlled ? !!externalLoading : internalLoading;

  const loadInternal = useCallback(async () => {
    try {
      setInternalLoading(true);
      const [agentsRes, typesRes] = await Promise.all([apiService.agents.getAll(), apiService.agents.getTypes()]);
      setInternalAgents(agentsRes.data.agents || []);
      setInternalTypes(typesRes.data.types || []);
      setError(null);
    } catch (err) {
      setError(`Failed to load agent configurations: ${describeApiError(err).message}`);
    } finally {
      setInternalLoading(false);
    }
  }, []);

  const reload = useCallback(() => (controlled && onReload ? onReload() : loadInternal()), [controlled, onReload, loadInternal]);

  useEffect(() => {
    if (!controlled) loadInternal();
  }, [controlled, loadInternal]);

  const formatHHMM = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const idOf = (agent) => agent.id || agent._id;
  const setAgentBusy = (agent, action) => setBusy((prev) => ({ ...prev, [idOf(agent)]: action }));

  const handleTestConnection = async (agent) => {
    try {
      setAgentBusy(agent, 'test');
      const res = await apiService.agents.testConnection(idOf(agent));
      if (res.data.success) {
        setNotice(`${agent.name}: connection successful`);
        setError(null);
      } else {
        setError(`${agent.name}: connection test failed – ${res.data.error || res.data.message || 'Unknown error'}`);
      }
      await reload();
    } catch (err) {
      setError(`${agent.name}: connection test failed – ${describeApiError(err).message}`);
    } finally {
      setAgentBusy(agent, null);
    }
  };

  const handleIngest = async (agent) => {
    try {
      setAgentBusy(agent, 'ingest');
      const res = await apiService.agents.ingest(idOf(agent), {});
      setIngestResult({ agent, ...res.data });
      await reload();
    } catch (err) {
      setError(`${agent.name}: ingestion failed – ${describeApiError(err).message}`);
    } finally {
      setAgentBusy(agent, null);
    }
  };

  const handleToggleEnabled = async (agent, isEnabled) => {
    try {
      setAgentBusy(agent, 'toggle');
      await apiService.agents.update(idOf(agent), { isEnabled });
      await reload();
    } catch (err) {
      setError(`${agent.name}: failed to ${isEnabled ? 'enable' : 'disable'} – ${describeApiError(err).message}`);
    } finally {
      setAgentBusy(agent, null);
    }
  };

  const performDelete = async (agent, force) => {
    try {
      setAgentBusy(agent, 'delete');
      await apiService.agents.delete(idOf(agent), force);
      setAgentToDelete(null);
      setForceDeleteInfo(null);
      setNotice(`${agent.name} deleted`);
      await reload();
    } catch (err) {
      const data = err.response && err.response.data;
      if (!force && err.response && err.response.status === 400 && data && data.canForceDelete) {
        setForceDeleteInfo({ agent, count: data.enrichmentLogCount, message: data.message });
        setAgentToDelete(null);
      } else {
        setError(`Failed to delete ${agent.name}: ${describeApiError(err).message}`);
        setAgentToDelete(null);
        setForceDeleteInfo(null);
      }
    } finally {
      setAgentBusy(agent, null);
    }
  };

  const handleEdit = (agent) => {
    setSelectedAgent(agent);
    setConfigDialogOpen(true);
  };

  const handleDialogClose = (shouldReload) => {
    setConfigDialogOpen(false);
    setSelectedAgent(null);
    if (shouldReload) reload();
  };

  const renderConnection = (agent) => {
    const status = agent.status || {};
    if (!status.lastConnectionTest) {
      return (
        <Box display="flex" alignItems="center">
          <UnknownIcon color="disabled" fontSize="small" />
          <Typography variant="body2" sx={{ ml: 1 }}>Not tested</Typography>
        </Box>
      );
    }
    return (
      <Tooltip title={`Last tested ${formatDateTime(status.lastConnectionTest)}`}>
        <Box display="flex" alignItems="center">
          {status.isConnected ? <ConnectedIcon color="success" fontSize="small" /> : <DisconnectedIcon color="error" fontSize="small" />}
          <Typography variant="body2" sx={{ ml: 1 }}>
            {status.isConnected ? 'Connected' : 'Disconnected'}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  const renderRateLimit = (agent) => {
    const rl = agent.status && agent.status.rateLimit;
    if (!rl || !rl.until || new Date(rl.until) <= new Date()) return null;
    return (
      <Tooltip title={rl.message || 'This integration is being rate limited'}>
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={`Rate limited until ${formatHHMM(rl.until)}`}
        />
      </Tooltip>
    );
  };

  if (loading && agents.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!hideHeader && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" component="h2">Agents</Typography>
            <Typography variant="body2" color="text.secondary">
              Connect your EDR platforms to ingest alerts, enrich detections and act on endpoints.
            </Typography>
          </Box>
          {canManageAgents && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleEdit(null)}>Add agent</Button>
          )}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {agents.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>No agents configured</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add a CrowdStrike, SentinelOne or Microsoft Defender integration to get started.
            </Typography>
            {canManageAgents && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleEdit(null)}>Add your first agent</Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {agents.map((agent) => {
            const id = idOf(agent);
            const action = busy[id];
            const status = agent.status || {};
            const stats = status.stats || {};
            const successRate = computeSuccessRate(agent);
            const caps = capabilitiesForType(agent.type, types);
            const lastError = status.lastError && status.lastError.message;
            return (
              <Grid item xs={12} md={6} lg={4} key={id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: agent.isEnabled ? 1 : 0.7 }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      {getVendorIcon(agent.type)}
                      <Box sx={{ ml: 1, flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="h6" noWrap title={agent.name}>{agent.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{getVendorLabel(agent.type, types)}</Typography>
                      </Box>
                      <Tooltip title={agent.isEnabled ? 'Disable' : 'Enable'}>
                        <span>
                          <Switch
                            size="small"
                            checked={!!agent.isEnabled}
                            disabled={!canManageAgents || !!action}
                            onChange={(e) => handleToggleEnabled(agent, e.target.checked)}
                          />
                        </span>
                      </Tooltip>
                    </Box>

                    {agent.description && (
                      <Typography variant="body2" sx={{ mb: 1.5 }} color="text.secondary">{agent.description}</Typography>
                    )}

                    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      {renderConnection(agent)}
                      {renderRateLimit(agent)}
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      Last ingestion: {status.lastSuccessfulIngestion ? formatRelativeTime(status.lastSuccessfulIngestion) : 'never'}
                      {stats.totalIngestions ? ` (${stats.totalIngestions} run(s), ${stats.totalDetectionsIngested || 0} detections)` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enrichments: {stats.totalEnrichments || 0}
                      {' · '}Success rate: {successRate === null ? 'n/a' : `${successRate}%`}
                    </Typography>
                    {lastError && (
                      <Tooltip title={status.lastError.timestamp ? formatDateTime(status.lastError.timestamp) : ''}>
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }} noWrap>
                          Last error: {lastError}
                        </Typography>
                      </Tooltip>
                    )}

                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Object.entries(caps).filter(([, v]) => v).map(([k]) => (
                        <Chip key={k} size="small" variant={k === 'alertStatus' && agent.settings?.sync?.pushStatusToVendor ? 'filled' : 'outlined'} color={k === 'alertStatus' && agent.settings?.sync?.pushStatusToVendor ? 'primary' : 'default'} label={k === 'alertStatus' ? `Alert status sync: ${agent.settings?.sync?.pushStatusToVendor ? 'on' : 'off'}` : (CAPABILITY_LABELS[k] || k)} />
                      ))}
                    </Box>
                  </CardContent>

                  <CardActions>
                    {canManageAgents && (
                      <>
                        <Tooltip title="Test connection">
                          <span>
                            <IconButton size="small" onClick={() => handleTestConnection(agent)} disabled={!!action}>
                              {action === 'test' ? <CircularProgress size={20} /> : <TestIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={caps.ingestion === false ? 'Ingestion not supported' : 'Ingest now'}>
                          <span>
                            <IconButton size="small" onClick={() => handleIngest(agent)} disabled={!!action || caps.ingestion === false || !agent.isEnabled}>
                              {action === 'ingest' ? <CircularProgress size={20} /> : <IngestIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton size="small" onClick={() => handleEdit(agent)} disabled={!!action}><EditIcon /></IconButton>
                          </span>
                        </Tooltip>
                        <Box sx={{ flexGrow: 1 }} />
                        <Tooltip title="Delete">
                          <span>
                            <IconButton size="small" color="error" onClick={() => setAgentToDelete(agent)} disabled={!!action}>
                              {action === 'delete' ? <CircularProgress size={20} /> : <DeleteIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <AgentConfigurationDialog open={configDialogOpen} onClose={handleDialogClose} agent={selectedAgent} />

      {/* Delete confirmation */}
      <Dialog open={!!agentToDelete} onClose={() => setAgentToDelete(null)}>
        <DialogTitle>Delete agent</DialogTitle>
        <DialogContent>
          <Typography>
            Delete "{agentToDelete && agentToDelete.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgentToDelete(null)}>Cancel</Button>
          <Button onClick={() => performDelete(agentToDelete, false)} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Force delete confirmation */}
      <Dialog open={!!forceDeleteInfo} onClose={() => setForceDeleteInfo(null)}>
        <DialogTitle>Force delete agent</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {forceDeleteInfo && forceDeleteInfo.message}
          </Alert>
          <Typography>
            Force deleting "{forceDeleteInfo && forceDeleteInfo.agent.name}" permanently removes the agent and its
            {' '}{forceDeleteInfo && forceDeleteInfo.count} enrichment log(s). Consider disabling it instead to keep the history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceDeleteInfo(null)}>Cancel</Button>
          <Button
            onClick={() => {
              const agent = forceDeleteInfo.agent;
              setForceDeleteInfo(null);
              handleToggleEnabled(agent, false);
            }}
          >
            Disable instead
          </Button>
          <Button onClick={() => performDelete(forceDeleteInfo.agent, true)} color="error" variant="contained">Force delete</Button>
        </DialogActions>
      </Dialog>

      {/* Ingest result */}
      <Dialog open={!!ingestResult} onClose={() => setIngestResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Ingestion result – {ingestResult && ingestResult.agent.name}</DialogTitle>
        <DialogContent>
          {ingestResult && (
            <>
              <Alert severity={ingestResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>{ingestResult.message}</Alert>
              {ingestResult.result && (
                <List dense>
                  <ListItem><ListItemText primary="Alerts processed" secondary={ingestResult.result.alertsProcessed || 0} /></ListItem>
                  <ListItem><ListItemText primary="Detections created" secondary={ingestResult.result.detectionsCreated || 0} /></ListItem>
                  <ListItem><ListItemText primary="Detections updated" secondary={ingestResult.result.detectionsUpdated || 0} /></ListItem>
                  <ListItem><ListItemText primary="Skipped (already ingested)" secondary={ingestResult.result.skipped || 0} /></ListItem>
                  {Array.isArray(ingestResult.result.errors) && ingestResult.result.errors.length > 0 && (
                    <ListItem>
                      <ListItemText
                        primary={`Errors (${ingestResult.result.errors.length})`}
                        secondary={ingestResult.result.errors.slice(0, 10).map((e) => (typeof e === 'string' ? e : e.message || JSON.stringify(e))).join('; ')}
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIngestResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentSettings;
